import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions {
  /** Root margin - how far before the element enters viewport to trigger (default: '200px') */
  rootMargin?: string;
  /** Only trigger once and never unobserve (default: true) */
  once?: boolean;
  /** Intersection threshold 0-1 (default: 0) */
  threshold?: number;
}

/**
 * Intersection Observer hook for viewport-based lazy mounting.
 * Returns [ref, hasBeenInView] — attach ref to a wrapper div,
 * render children only when hasBeenInView is true.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {}
): [React.RefObject<T>, boolean] {
  const { rootMargin = '200px', once = true, threshold = 0 } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If already in view (e.g. SSR hydration), skip observer
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 200) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, once, threshold]);

  return [ref, inView];
}
